import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import useScrollToTop from '@/hooks/useScrollToTop';
import { getComicsByCategory, getCategoryName, categories } from '@/mocks/comics';

const PAGE_SIZE = 8;

export default function CategoryPage() {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();
  const categoryName = getCategoryName(slug || '');
  const comics = useMemo(() => getComicsByCategory(slug || ''), [slug]);

  const [statusFilter, setStatusFilter] = useState<string>('全部');
  const [sortBy, setSortBy] = useState<string>('latest');
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = useMemo(() => {
    let result = [...comics];
    if (statusFilter === '连载中') result = result.filter((c) => c.status === '连载中');
    if (statusFilter === '已完结') result = result.filter((c) => c.status === '已完结');
    if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'latest') result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    if (sortBy === 'chapters') result.sort((a, b) => b.chapters - a.chapters);
    return result;
  }, [comics, statusFilter, sortBy]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageUrl = `http://m99re.com/category/${slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${categoryName}漫画 - 韩国漫画网`,
    url: pageUrl,
    description: `韩国漫画网${categoryName}漫画分类，提供${comics.length}部优质${categoryName}题材韩漫在线免费阅读。`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'http://m99re.com/',
    },
    breadcrumb: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://m99re.com/' },
        { '@type': 'ListItem', position: 2, name: `${categoryName}漫画`, item: pageUrl },
      ],
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />
      <main>
        <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium whitespace-nowrap">{categoryName}漫画</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
              {categoryName}漫画
            </h1>
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-foreground-800">{comics.length}</strong> 部{categoryName}题材漫画作品
            </p>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Sidebar */}
              <div className="lg:w-56 shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h3 className="text-xs font-label uppercase tracking-wider text-foreground-400 mb-4 hidden lg:block">切换分类</h3>
                  <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          cat.slug === slug
                            ? 'bg-primary-500 text-background-50 font-medium'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <i className={`${cat.icon} w-5 h-5 flex items-center justify-center`}></i>
                        <span className="flex-1">{cat.name}</span>
                        <span className={`text-xs ${cat.slug === slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                          {cat.count}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-400 whitespace-nowrap">状态：</span>
                    {['全部', '连载中', '已完结'].map((s) => (
                      <button
                        key={s}
                        onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          statusFilter === s
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
                    <select
                      value={sortBy}
                      onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 border border-background-200 text-foreground-600 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="latest">最近更新</option>
                      <option value="rating">评分最高</option>
                      <option value="chapters">章节最多</option>
                    </select>
                  </div>
                </div>

                {paginated.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                      {paginated.map((comic) => (
                        <Link
                          key={comic.id}
                          to={`/comic/${comic.id}`}
                          className="group block"
                        >
                          <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                            <img
                              src={comic.cover}
                              alt={comic.title}
                              title={`${comic.title} - 韩国漫画网`}
                              loading="lazy"
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-50/90 text-foreground-800">
                              {comic.status}
                            </div>
                          </div>
                          <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                            {comic.title}
                          </h3>
                          <p className="text-xs text-foreground-400 whitespace-nowrap">
                            {comic.author} · 共{comic.chapters}话
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <i className="ri-star-fill text-[10px] text-accent-500 w-3 h-3 flex items-center justify-center"></i>
                            <span className="text-xs font-medium text-foreground-700">{comic.rating}</span>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-10">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-colors cursor-pointer ${
                            currentPage === 1
                              ? 'text-foreground-300 cursor-not-allowed'
                              : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                          <button
                            key={p}
                            onClick={() => handlePageChange(p)}
                            className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              p === currentPage
                                ? 'bg-primary-500 text-background-50'
                                : 'text-foreground-600 hover:bg-background-100'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-colors cursor-pointer ${
                            currentPage === totalPages
                              ? 'text-foreground-300 cursor-not-allowed'
                              : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-20">
                    <i className="ri-file-search-line text-4xl text-foreground-300 mb-4 block"></i>
                    <p className="text-foreground-500 text-sm">暂无符合条件的漫画作品</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}